import { TextField, Button, MenuItem, Stack, Typography } from "@mui/material";
import dayjs from "dayjs";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAsignaturas } from "../../hooks/useAsignaturas";
import { useSolicitarFormacion } from "../../hooks/useSolicitarFormacion";
import { useProfesoresAFormar } from "../../hooks/useProfesoresAFormar";
import { useProfesoresCandidatos } from "../../hooks/useProfesoresCandidatos";
import toast from "react-hot-toast";

/* ─────────────────────────────────────────────
 * Props
 *   preset   → valores para precargar el formulario
 *   onClose  → callback opcional (por ejemplo, para cerrar un diálogo)
 * ───────────────────────────────────────────── */
interface Props {
  preset?: Partial<{
    idAsignatura: number;
    idProfesor: number;
    fechaLimite: string;
  }>;
  onClose?: () => void;
}

/* ═════════ COMPONENTE ═════════ */
export default function FormularioFormacion({ preset = {}, onClose }: Props) {
  const { t } = useTranslation<"translation">(); // 🌐 i18n hook

  /* ───── catálogos ───── */
  const { rows: asignaturas } = useAsignaturas();
  const { mutateAsync, isPending } = useSolicitarFormacion();

  /* ───── estado controlado ───── */
  const [idAsignatura, setIdAsignatura] = useState<number | "">(preset.idAsignatura ?? "");
  const [idProfesor, setIdProfesor] = useState<number | "">(preset.idProfesor ?? "");
  const [idFormador, setIdFormador] = useState<number | "">("");
  const [fechaLimite, setFechaLimite] = useState<string>(
    preset.fechaLimite ?? dayjs().add(7, "day").format("YYYY-MM-DD")
  );

  /* ───── hooks dependientes ───── */
  const { data: profesores = [], isFetching: loadingProfesores } =
    useProfesoresAFormar(idAsignatura);
  const { data: formadores = [], isFetching: loadingFormadores } =
    useProfesoresCandidatos(idAsignatura);

  /* ───── submit ───── */
  const handleSubmit = async () => {
    if (!idAsignatura || !idProfesor || !idFormador || !fechaLimite) {
      toast.error(t<string>("training.form.error.missingFields"));
      return;
    }

    try {
      await mutateAsync({ idAsignatura, idProfesor, idFormador, fechaLimite });
      toast.success(t<string>("training.form.success"));
      onClose?.();
      // opcional: limpiar formulario si el componente se seguirá mostrando
      setIdAsignatura("");
      setIdProfesor("");
      setIdFormador("");
      setFechaLimite(dayjs().add(7, "day").format("YYYY-MM-DD"));
    } catch (err: any) {
      toast.error(err?.response?.data?.message || t<string>("training.form.error.api"));
    }
  };

  /* ═════════ UI ═════════ */
  return (
    <Stack spacing={2} maxWidth={400}>
      <Typography variant="h6">{t("training.form.title")}</Typography>

      {/* Asignatura */}
      <TextField
        select
        label={t("training.form.field.subject.label")}
        value={idAsignatura}
        onChange={(e) => {
          setIdAsignatura(Number(e.target.value));
          // reset dependientes
          setIdProfesor("");
          setIdFormador("");
        }}
        required
      >
        {asignaturas.map((a) => (
          <MenuItem key={a.id} value={a.id}>
            {a.nombre}
          </MenuItem>
        ))}
      </TextField>

      {/* Profesor a formar */}
      <TextField
        select
        label={t("training.form.field.trainee.label")}
        value={idProfesor}
        onChange={(e) => setIdProfesor(Number(e.target.value))}
        required
        disabled={!idAsignatura || loadingProfesores}
      >
        {profesores.length === 0 && (
          <MenuItem disabled value="">
            {loadingProfesores ? t("training.form.loading") : t("training.form.noneCandidates")}
          </MenuItem>
        )}
        {profesores.map((p) => (
          <MenuItem key={p.id} value={p.id}>
            {p.nombre}
          </MenuItem>
        ))}
      </TextField>

      {/* Formador */}
      <TextField
        select
        label={t("training.form.field.trainer.label")}
        value={idFormador}
        onChange={(e) => setIdFormador(Number(e.target.value))}
        required
        disabled={!idAsignatura || loadingFormadores}
      >
        {formadores.length === 0 && (
          <MenuItem disabled value="">
            {loadingFormadores ? t("training.form.loading") : t("training.form.noneTrainers")}
          </MenuItem>
        )}
        {formadores.map((f) => (
          <MenuItem key={f.id} value={f.id}>
            {f.nombre}
          </MenuItem>
        ))}
      </TextField>

      {/* Fecha límite */}
      <TextField
        label={t("training.form.field.deadline.label")}
        type="date"
        value={fechaLimite}
        onChange={(e) => setFechaLimite(e.target.value)}
        InputLabelProps={{ shrink: true }}
        required
      />

      {/* Botón submit */}
      <Button variant="contained" onClick={handleSubmit} disabled={isPending}>
        {isPending ? t("training.form.button.submitting") : t("training.form.button.submit")}
      </Button>
    </Stack>
  );
}
